<app-dialog [size]="dialogSize()">
  <app-dialog-header [emitCloseEvent]="true" (closeDialog)="cancel()">{{ title }}</app-dialog-header>

  @if (showRebuildNotice()) {
    <app-inline-banner dataSelector="rebuild-notice" variant="info" class="mb-4">
      <div class="w-100">
        <p class="body-300">
          The original email as it was delivered is no longer available. A faithful reconstruction will be sent instead, re-created
          from the original template and the data saved when the email was first sent — the same amounts, details, and links. If the
          email's template has changed since then, the resent email will use the current design.
        </p>
        <mat-checkbox
          dataSelector="rebuild-ack-checkbox"
          class="mt-2"
          [checked]="rebuildAcknowledged()"
          (change)="rebuildAcknowledged.set($event.checked)"
        >
          I understand a reconstruction of the original email will be sent.
        </mat-checkbox>
        <div class="d-flex justify-content-end">
          <div [tooltip]="rebuildOptOutTooltip" [tooltipEnabled]="!rebuildAcknowledged()">
            <button
              dataSelector="rebuild-dont-ask-again-button"
              type="button"
              [disabled]="!rebuildAcknowledged()"
              (click)="onRebuildNoticeOptOut()"
              andButton
              color="ghost-secondary"
            >
              Don't ask again
            </button>
          </div>
        </div>
      </div>
    </app-inline-banner>
  }

  <div class="ta--center resend-add-mb-if-valid">
    <p class="body-300">{{ primaryMessage() }}</p>
    <p class="body-300 mt-2">{{ secondaryMessage }}</p>
  </div>

  @if (hasError()) {
    <div #errorAlert role="alert" class="alert alert--error alert--dismissible my-4">
      <p class="body-300">{{ errorMessage() }}</p>
      <button
        dataSelector="dismiss-error-button"
        aria-label="Dismiss error message"
        type="button"
        class="alert__close"
        (click)="errorMessage.set(null)"
      >
        <anduin-icon name="x" aria-hidden="true" fill="none" stroke="currentColor" />
      </button>
    </div>
  }

  <app-recipient-emails
    [allowMultiplePrimary]="true"
    [bccEmails]="initialBccEmails()"
    [ccEmails]="initialCcEmails()"
    [disabled]="!canEditRecipients"
    [pendingUnblockEmails]="pendingUnblockEmails()"
    [primaryEmails]="initialToEmails()"
    [showBcc]="true"
    [showCc]="true"
    [suppressedEmails]="suppressedEmails()"
    (bccEmailsChanged)="onBccEmailsChanged($event)"
    (ccEmailsChanged)="onCcEmailsChanged($event)"
    (isValid)="onRecipientsValidChanged($event)"
    (primaryEmailsChanged)="onPrimaryEmailsChanged($event)"
    (unblockRequested)="onUnblockRequested($event)"
    (undoUnblockRequested)="onUndoUnblockRequested($event)"
    primaryHint=""
    primaryLabel="To:"
  />

  @if (!canEditRecipients) {
    <p class="small-300 color-gray-400 mt-4">
      @if (emailSettingDisplay) {
        Editing recipients is disabled for {{ emailSettingDisplay }} emails.
      } @else {
        Editing recipients is disabled for this email type.
      }
    </p>
  }

  @if (showEmailContent()) {
    <div dataSelector="resend-email-content" class="email-content mt-6">
      @if (canEditContent()) {
        <div class="d-flex align-items-center">
          <span class="content-label flex-shrink-1 color-gray-300">Subject:</span>
          <input dataSelector="resend-subject-input" aria-label="Email subject" class="form-input" [formControl]="subjectControl" />
        </div>
        <div class="mt-4">
          <span class="content-label color-gray-300">Message:</span>
          <aiwyn-editor dataSelector="resend-body-prefix-editor" class="my-1" [formControl]="bodyPrefixControl" [init]="editorConfig" />
          <p class="small-300 color-gray-400">
            This message is added above the original email. The original email content cannot be changed.
          </p>
        </div>
      }

      <div class="email-preview-container py-4 mt-4">
        @if (previewLoading()) {
          <mat-spinner class="mx-auto m-4" />
        } @else if (previewHtml()) {
          <div dataSelector="resend-email-preview" class="email-preview" [innerHtml]="previewHtml()"></div>
        } @else {
          <p class="ta--center body-300">Email preview not available</p>
        }
      </div>
    </div>
  }

  @if (contentErrorMessage()) {
    <div role="alert" class="alert alert--error alert--dismissible my-2">
      <p class="body-300">{{ contentErrorMessage() }}</p>
      <button
        dataSelector="dismiss-content-error-button"
        aria-label="Dismiss content error message"
        type="button"
        class="alert__close"
        (click)="contentErrorMessage.set(null)"
      >
        <anduin-icon name="x" aria-hidden="true" fill="none" stroke="currentColor" />
      </button>
    </div>
  }

  @if (showAttachments()) {
    <div class="attachment-info d-flex flex-wrap mt-4">
      @for (attachment of existingAttachments(); track attachment.fileId) {
        <div class="attachment-item d-flex align-items-center ps-2 pe-1 py-1 bg-color-gray-1000">
          <span>{{ attachment.filename }}</span>
          <button
            dataSelector="download-attachment-button"
            type="button"
            class="d-flex align-items-center ms-1"
            [attr.aria-label]="'Download ' + attachment.filename"
            [disabled]="downloadingFileId() !== null"
            (click)="downloadAttachment(attachment)"
            tooltip="Download attachment"
          >
            @if (downloadingFileId() === attachment.fileId) {
              <mat-spinner diameter="16" />
            } @else {
              <anduin-icon name="download" fill="none" stroke="currentColor" />
            }
          </button>
        </div>
      }
      @for (attachment of newAttachments(); track attachment.fileId) {
        <div class="attachment-item d-flex align-items-center ps-2 pe-1 py-1 bg-color-green-1000">
          <span>{{ attachment.fileName }}</span>
          <button
            dataSelector="remove-attachment-button"
            type="button"
            class="d-flex align-items-center ms-1"
            [attr.aria-label]="'Remove ' + attachment.fileName"
            (click)="removeNewAttachment(attachment.fileId)"
          >
            <anduin-icon name="x" fill="none" stroke="currentColor" />
          </button>
        </div>
      }
    </div>
  }

  @if (canEditContent()) {
    @if (attachmentError()) {
      <div role="alert" class="alert alert--error alert--dismissible mt-4">
        <p class="body-300">{{ attachmentError() }}</p>
        <button
          dataSelector="dismiss-attachment-error-button"
          aria-label="Dismiss attachment error message"
          type="button"
          class="alert__close"
          (click)="attachmentError.set(null)"
        >
          <anduin-icon name="x" aria-hidden="true" fill="none" stroke="currentColor" />
        </button>
      </div>
    }
    @if (testSendStatus() === 'success') {
      <div dataSelector="send-test-success" role="alert" class="alert alert--success alert--dismissible mt-4">
        <p class="body-300">A test email was sent to your email address.</p>
        <button
          dataSelector="dismiss-send-test-success-button"
          aria-label="Dismiss test email confirmation"
          type="button"
          class="alert__close"
          (click)="testSendStatus.set(null)"
        >
          <anduin-icon name="x" aria-hidden="true" fill="none" stroke="currentColor" />
        </button>
      </div>
    } @else if (testSendStatus() === 'error') {
      <div dataSelector="send-test-error" role="alert" class="alert alert--error alert--dismissible mt-4">
        <p class="body-300">Failed to send the test email. Please try again.</p>
        <button
          dataSelector="dismiss-send-test-error-button"
          aria-label="Dismiss test email error message"
          type="button"
          class="alert__close"
          (click)="testSendStatus.set(null)"
        >
          <anduin-icon name="x" aria-hidden="true" fill="none" stroke="currentColor" />
        </button>
      </div>
    }
    <div class="d-flex mt-4">
      <button
        dataSelector="add-attachment-button"
        class="me-4"
        [isLoading]="isUploadingAttachments()"
        (click)="attachmentInput.click()"
        andButton
        color="ghost-secondary"
      >
        <anduin-icon name="paperclip" class="me-2" fill="none" stroke="currentColor" />
        Add Attachment
      </button>
      <input #attachmentInput type="file" class="d-none" (input)="onAttachmentsSelected(attachmentInput.files, attachmentInput)" multiple />

      <button
        dataSelector="send-test-button"
        class="me-4"
        [disabled]="isUploadingAttachments()"
        [isLoading]="isSendingTest()"
        (click)="onSendTest()"
        andButton
        color="ghost-secondary"
      >
        <anduin-icon name="send" class="me-2" fill="none" stroke="currentColor" />
        Send Myself a Test
      </button>
      <button
        dataSelector="refresh-preview-button"
        class="ms-auto"
        [isLoading]="previewLoading()"
        (click)="refreshPreview()"
        andButton
        color="ghost-secondary"
      >
        Refresh Preview
      </button>
    </div>
  }

  <app-dialog-footer>
    <button
      id="confirm-button"
      class="ms-2"
      [disabled]="isResendDisabled()"
      [isLoading]="isResending() || isValidating()"
      (click)="onResend()"
      andButton
      color="primary"
    >
      {{ hasError() ? retryLabel : confirmLabel }}
    </button>
  </app-dialog-footer>
</app-dialog>
